import { matchPath } from 'react-router-dom'

import { ListTerrotoriesSortOrder } from 'generated/graphql'
import type { ListTerrotoriesSortFields } from 'generated/graphql'
import type { INormalizedDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'
import { GLOBAL_TAXONOMY_PATHNAMES_BY_TYPE } from 'utils/territoryUtils/consts'

import {
  GLOBAL_TAXONOMY_TERRITORY_TYPES,
  GLOBAL_TAXONOMY_TYPES_WITH_CHILDREN,
  TERRITORY_TRAIL_PARAM,
  TERRITORY_TRAIL_SEPARATOR,
} from './global-topic.consts'
import type { IGlobalTaxonomyTerritoryGate, TTableOrder } from './global-topic.types'

export const handleSort = (data: INormalizedDataForTerritoriesTable[], order: TTableOrder) => {
  const sortField = order.field
  return data.sort((a, b) => {
    if (order.order === ListTerrotoriesSortOrder.Asc) {
      return a[sortField] - b[sortField]
    }
    return b[sortField] - a[sortField]
  })
}

export const getNextTableOrder = (previousOrder: TTableOrder, field: ListTerrotoriesSortFields): TTableOrder => {
  const toggledOrder =
    previousOrder.order === ListTerrotoriesSortOrder.Desc ? ListTerrotoriesSortOrder.Asc : ListTerrotoriesSortOrder.Desc
  return {
    field,
    order: previousOrder.field === field ? toggledOrder : ListTerrotoriesSortOrder.Desc,
  }
}

export const getIsGlobalTaxonomyTerritory = ({
  isGlobalTaxonomyEnabled,
  territoryType,
}: IGlobalTaxonomyTerritoryGate) => isGlobalTaxonomyEnabled && GLOBAL_TAXONOMY_TERRITORY_TYPES.includes(territoryType)

export const getHasGlobalTaxonomyChildren = ({
  isGlobalTaxonomyEnabled,
  territoryType,
}: IGlobalTaxonomyTerritoryGate) =>
  isGlobalTaxonomyEnabled && GLOBAL_TAXONOMY_TYPES_WITH_CHILDREN.includes(territoryType)

const isTrailTerritoryId = (territoryId: string) => {
  const [type, id] = territoryId.split('.')

  return GLOBAL_TAXONOMY_TYPES_WITH_CHILDREN.includes(type) && Boolean(id)
}

/** Only theme, segment and subject ids count as levels: anything else in the parameter is dropped. */
export const parseTerritoryTrail = (search: string): string[] =>
  new URLSearchParams(search).get(TERRITORY_TRAIL_PARAM)?.split(TERRITORY_TRAIL_SEPARATOR).filter(isTrailTerritoryId) ??
  []

/** The query string that carries `trailIds` to the next page, or an empty string when there is no trail. */
export const getTerritoryTrailSearch = (trailIds: string[]): string =>
  trailIds.length ? `?${TERRITORY_TRAIL_PARAM}=${trailIds.map(encodeURIComponent).join(TERRITORY_TRAIL_SEPARATOR)}` : ''

/** The `type.id` of the Global Taxonomy territory a pathname points to, or `null` for any other page. */
export const getGlobalTaxonomyTerritoryIdFromPathname = (pathname: string): string | null => {
  for (const [type, path] of Object.entries(GLOBAL_TAXONOMY_PATHNAMES_BY_TYPE)) {
    const entityId = matchPath<{ entityId?: string }>(pathname, { path })?.params.entityId

    if (entityId) return `${type}.${entityId}`
  }

  return null
}
